import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from "recharts";
import { ClipboardList, CalendarClock, CheckCircle2, DollarSign, Sparkles, TrendingUp } from "lucide-react";
import { STATUS_OPTIONS } from "@/lib/dashboardApi";

const ORANGE = "#F97316";

const KpiCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4 shadow-sm" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}1f` }}>
      <Icon className="w-6 h-6" style={{ color: accent }} />
    </div>
    <div>
      <div className="text-2xl font-bold text-slate-900 leading-none">{value}</div>
      <div className="text-slate-500 text-sm mt-1">{label}</div>
    </div>
  </div>
);

const Overview = ({ stats, bookings, renewals = [] }) => {
  if (!stats) return null;
  const serviceData = Object.entries(stats.by_service || {}).map(([name, value]) => ({
    name: name.replace(" / ", " ").replace("Installation", "Install"),
    value,
  }));
  const statusData = STATUS_OPTIONS
    .map((s) => ({ name: s.label, value: stats.by_status?.[s.value] || 0, color: s.color }))
    .filter((s) => s.value > 0);
  const recent = (bookings || []).slice(0, 6);
  return (
    <div className="space-y-6" data-testid="overview-charts">
      {renewals.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="renewal-alert">
          <p className="text-sm font-bold text-amber-800 mb-2">⏰ {renewals.length} Comfort Club renewal{renewals.length > 1 ? "s" : ""} due within 30 days</p>
          <div className="flex flex-wrap gap-2">
            {renewals.slice(0, 8).map((r) => (
              <span key={r.key} className="text-xs bg-white border border-amber-200 rounded-full px-3 py-1 text-amber-800">{r.name} · {r.renewal_date}</span>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={ClipboardList} label="Total Requests" value={stats.total} accent={ORANGE} />
        <KpiCard icon={Sparkles} label="New / Unassigned" value={stats.new} accent="#0ea5e9" />
        <KpiCard icon={CalendarClock} label="Scheduled" value={stats.scheduled} accent="#2563eb" />
        <KpiCard icon={CheckCircle2} label="Completed" value={stats.completed} accent="#22c55e" />
        <KpiCard icon={TrendingUp} label="This Week" value={stats.this_week} accent="#14b8a6" />
        <KpiCard icon={CalendarClock} label="Today" value={stats.today} accent="#a855f7" />
        <KpiCard icon={DollarSign} label="Revenue Logged" value={`$${(stats.revenue || 0).toLocaleString()}`} accent={ORANGE} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-slate-900 font-semibold text-lg mb-4">Requests by Service</h3>
          {serviceData.length === 0 ? (
            <p className="text-slate-400 text-sm py-12 text-center">No data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={serviceData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#0f172a" }} cursor={{ fill: "rgba(15,23,42,0.04)" }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {serviceData.map((_, i) => (<Cell key={i} fill={ORANGE} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-slate-900 font-semibold text-lg mb-4">Status Breakdown</h3>
          {statusData.length === 0 ? (
            <p className="text-slate-400 text-sm py-12 text-center">No data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                  {statusData.map((s, i) => (<Cell key={i} fill={s.color} />))}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 12, color: "#64748b" }} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#0f172a" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <h3 className="text-slate-900 font-semibold text-lg mb-4">Recent Requests</h3>
        {recent.length === 0 ? (
          <p className="text-slate-400 text-sm py-8 text-center">No requests yet</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {recent.map((b) => (
              <div key={b.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-slate-900 font-medium">{b.name}</p>
                  <p className="text-slate-500 text-sm">{b.service} &bull; {b.phone}</p>
                </div>
                <span className="text-xs px-3 py-1 rounded-full font-semibold capitalize"
                  style={{ backgroundColor: `${(STATUS_OPTIONS.find(s => s.value === b.status)?.color || '#64748b')}1f`, color: STATUS_OPTIONS.find(s => s.value === b.status)?.color || '#64748b' }}>
                  {(b.status || "new").replace("_", " ")}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Overview;
